import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  Bell, Check, CheckCheck, X, Briefcase, MessageCircle,
  MapPin, Star, DollarSign, AlertCircle, ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Notification type icons
const notificationIcons = {
  job_status_change: Briefcase,
  job_assigned: Check,
  job_completed: CheckCheck,
  job_cancelled: X,
  new_message: MessageCircle,
  contractor_arrived: MapPin,
  contractor_en_route: MapPin,
  review_reminder: Star,
  payment_received: DollarSign,
  new_job_offer: Briefcase,
  system: AlertCircle,
};

// Single notification item
const NotificationItem = ({ notification, onRead, onNavigate }) => {
  const Icon = notificationIcons[notification.type] || AlertCircle;
  
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleClick = () => {
    if (!notification.read) {
      onRead(notification.id);
    }
    if (notification.action_url) {
      onNavigate(notification.action_url);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 10 }}
      className={`px-4 py-3 border-b border-zinc-100 last:border-0 cursor-pointer hover:bg-zinc-50 transition-colors ${
        !notification.read ? 'bg-blue-50/50' : ''
      }`}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
          !notification.read ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-500'
        }`}>
          <Icon className="w-4 h-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${!notification.read ? 'font-semibold text-zinc-900' : 'text-zinc-700'}`}>
            {notification.title}
          </p>
          <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">
            {notification.message}
          </p>
          <p className="text-xs text-zinc-400 mt-1">
            {formatTime(notification.created_at)}
          </p>
        </div>
        
        {!notification.read && (
          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
        )}
      </div>
    </motion.div>
  );
};

const NotificationBell = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      setupWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const setupWebSocket = () => {
    if (!token) return;
    
    try {
      // The backend mounts the socket at /ws/{token}, NOT under /api - every
      // other page derives it from REACT_APP_BACKEND_URL. This used to build
      // /api/ws/{token} off window.location, which never matched a route.
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsBase = (process.env.REACT_APP_BACKEND_URL || `${wsProtocol}//${window.location.host}`)
        .replace('https://', 'wss://')
        .replace('http://', 'ws://');
      const wsUrl = `${wsBase}/ws/${token}`;
      
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.event === 'notification') {
            // Add new notification to the top
            setNotifications(prev => [data.data, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            // Show toast notification
            toast(data.data.title, {
              description: data.data.message,
              action: data.data.action_url ? {
                label: 'View',
                onClick: () => navigate(data.data.action_url)
              } : undefined,
            });
          }
        } catch (e) {
          console.error('WS message parse error:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        // Reconnect after 5 seconds
        setTimeout(setupWebSocket, 5000);
      };
    } catch (e) {
      console.error('WebSocket setup error:', e);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/notifications?limit=20`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await axios.post(`${API_URL}/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API_URL}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all read:', error);
    }
  };

  const handleNavigate = (url) => {
    setOpen(false);
    navigate(url);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        className="relative text-zinc-600 hover:text-zinc-900"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 bg-white rounded-xl border border-zinc-200 shadow-lg overflow-hidden z-50"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between bg-zinc-50">
              <h3 className="font-semibold text-zinc-900">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-zinc-500 hover:text-zinc-900"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Notifications List */}
            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="py-8 text-center text-zinc-500 text-sm">
                  Loading...
                </div>
              ) : notifications.length > 0 ? (
                notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRead={handleMarkRead}
                    onNavigate={handleNavigate}
                  />
                ))
              ) : (
                <div className="py-8 text-center">
                  <Bell className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
                  <p className="text-zinc-500 text-sm">No notifications</p>
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-4 py-2 border-t border-zinc-100 bg-zinc-50">
                <button
                  onClick={() => handleNavigate('/notifications')}
                  className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1 w-full justify-center"
                >
                  View all notifications
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
